import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileEdit,
  BookOpen,
  CheckCircle,
  FileSignature,
  Flag,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  WorkflowProductType,
  SpotTransitionApprovals,
  ForwardTransitionApprovals,
  SPOT_STATES,
  FORWARD_STATES,
  SPOT_TRANSITIONS,
  FORWARD_TRANSITIONS,
  TransitionInfo,
  StateInfo,
} from '../../types/workflow';

interface Props {
  productType: WorkflowProductType;
  approvals: SpotTransitionApprovals | ForwardTransitionApprovals;
  onApprovalChange: (transitionKey: string, value: number) => void;
  disabled?: boolean;
}

// Map icon names to Lucide components
const IconMap: Record<string, LucideIcon> = {
  FileEdit,
  BookOpen,
  CheckCircle,
  FileSignature,
  Flag,
  XCircle,
};

// Get color class based on approver count
const getApproverColorClass = (count: number): string => {
  switch (count) {
    case 0:
      return 'approvers-auto';
    case 1:
      return 'approvers-one';
    case 2:
      return 'approvers-two';
    default:
      return '';
  }
};

const WorkflowDiagram: React.FC<Props> = ({
  productType,
  approvals,
  onApprovalChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const states = productType === 'spot' ? SPOT_STATES : FORWARD_STATES;
  const transitions = productType === 'spot' ? SPOT_TRANSITIONS : FORWARD_TRANSITIONS;

  // Filter states and transitions
  const mainFlowStates = states.filter(s => s.state !== 'Cancelled');
  const cancelledState = states.find(s => s.state === 'Cancelled')!;
  const forwardTransitions = transitions.filter(t => !t.isRevert);
  const revertTransitions = transitions.filter(t => t.isRevert);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setActivePopover(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTransitionClick = (transitionKey: string) => {
    if (disabled) return;
    setActivePopover(activePopover === transitionKey ? null : transitionKey);
  };

  const handleApproverSelect = (transitionKey: string, value: number) => {
    onApprovalChange(transitionKey, value);
    setActivePopover(null);
  };

  // Get state index in main flow for positioning
  const getStateIndex = (stateName: string): number => {
    return mainFlowStates.findIndex(s => s.state === stateName);
  };

  // Convert state name to translation key (handles ContractSigned special case)
  const getStateTranslationKey = (stateName: string): string => {
    return stateName === 'ContractSigned' ? 'contractSigned' : stateName.toLowerCase();
  };

  const renderApproverPopover = (transitionKey: string, approverCount: number) => (
    <div className="approver-popover" ref={popoverRef}>
      <div className="popover-title">{t('settings.workflow.requiredApprovers')}</div>
      {[0, 1, 2].map((value) => (
        <button
          key={value}
          className={`popover-option ${approverCount === value ? 'selected' : ''} ${getApproverColorClass(value)}`}
          onClick={() => handleApproverSelect(transitionKey, value)}
        >
          <span className={`option-value ${getApproverColorClass(value)}`}>{value}</span>
          <span className="option-label">
            {value === 0 ? t('settings.workflow.autoApproval') :
             value === 1 ? t('settings.workflow.oneApprover') :
             t('settings.workflow.twoApprovers')}
          </span>
        </button>
      ))}
    </div>
  );

  const renderStateNode = (stateInfo: StateInfo, isCancelled: boolean = false) => {
    const IconComponent = IconMap[stateInfo.icon] || FileEdit;
    const stateKey = getStateTranslationKey(stateInfo.state);

    return (
      <div
        className={`state-node ${stateInfo.isTerminal ? 'terminal' : ''} ${stateInfo.state.toLowerCase()}`}
      >
        <div className="state-icon">
          <IconComponent size={20} />
        </div>
        <div className="state-label">{t(`settings.workflow.states.${stateKey}`)}</div>
      </div>
    );
  };

  const renderTransitionColumn = (forwardTransition: TransitionInfo, index: number) => {
    // Find the corresponding revert transition (goes backwards from the next state)
    const revertTransition = revertTransitions.find(t =>
      t.fromState === forwardTransition.toState && t.toState === forwardTransition.fromState
    );

    const fwdApproverCount = (approvals as unknown as Record<string, number>)[forwardTransition.key] ?? 0;
    const fwdColorClass = getApproverColorClass(fwdApproverCount);
    const fwdIsActive = activePopover === forwardTransition.key;

    const revApproverCount = revertTransition ? (approvals as unknown as Record<string, number>)[revertTransition.key] ?? 0 : 0;
    const revColorClass = getApproverColorClass(revApproverCount);
    const revIsActive = revertTransition ? activePopover === revertTransition.key : false;

    return (
      <div className="transition-column" key={forwardTransition.key}>
        {/* Forward transition - top section */}
        <div className="forward-section">
          <div className="badge-container">
            <button
              className={`transition-badge ${fwdColorClass} ${fwdIsActive ? 'active' : ''}`}
              onClick={() => handleTransitionClick(forwardTransition.key)}
              disabled={disabled}
              title={t('settings.workflow.clickToChange')}
            >
              {fwdApproverCount}
            </button>
            {fwdIsActive && renderApproverPopover(forwardTransition.key, fwdApproverCount)}
          </div>
          <div className="forward-arrow">
            <svg viewBox="0 0 80 20" preserveAspectRatio="none">
              <defs>
                <marker id={`fwd-arrow-${forwardTransition.key}`} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="var(--text-secondary)" />
                </marker>
              </defs>
              <line x1="5" y1="10" x2="68" y2="10" stroke="var(--text-secondary)" strokeWidth="2" markerEnd={`url(#fwd-arrow-${forwardTransition.key})`} />
            </svg>
          </div>
        </div>

        {/* Revert transition - bottom section (if exists) */}
        {revertTransition && (
          <div className="revert-section">
            <div className="revert-arrow">
              <svg viewBox="0 0 80 20" preserveAspectRatio="none">
                <defs>
                  <marker id={`rev-arrow-${revertTransition.key}`} markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6" fill="var(--text-secondary)" />
                  </marker>
                </defs>
                <line x1="68" y1="10" x2="5" y2="10" stroke="var(--text-secondary)" strokeWidth="2" strokeDasharray="4,2" markerEnd={`url(#rev-arrow-${revertTransition.key})`} />
              </svg>
            </div>
            <div className="badge-container">
              <button
                className={`transition-badge revert-badge ${revColorClass} ${revIsActive ? 'active' : ''}`}
                onClick={() => handleTransitionClick(revertTransition.key)}
                disabled={disabled}
                title={`${t(`settings.workflow.states.${getStateTranslationKey(revertTransition.fromState)}`)} → ${t(`settings.workflow.states.${getStateTranslationKey(revertTransition.toState)}`)}`}
              >
                {revApproverCount}
              </button>
              {revIsActive && renderApproverPopover(revertTransition.key, revApproverCount)}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="workflow-diagram">
      {/* Integrated diagram container */}
      <div className="integrated-diagram">
        {/* Main flow row - states with transition columns between them */}
        <div className="main-flow-row">
          {mainFlowStates.map((stateInfo, index) => (
            <React.Fragment key={stateInfo.state}>
              <div className="state-column">
                {renderStateNode(stateInfo)}
              </div>
              {index < mainFlowStates.length - 1 && renderTransitionColumn(forwardTransitions[index], index)}
            </React.Fragment>
          ))}
        </div>

        {/* Cancelled state - positioned to the right/below with connection from all non-final states */}
        <div className="cancelled-section">
          <div className="cancelled-connection">
            <svg className="cancelled-arrow-svg" viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet">
              <defs>
                <marker id="cancel-arrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="var(--accent-red)" />
                </marker>
              </defs>
              <path
                d="M 10 10 L 10 30 L 90 30"
                fill="none"
                stroke="var(--accent-red)"
                strokeWidth="2"
                strokeDasharray="4,2"
                markerEnd="url(#cancel-arrow)"
                opacity="0.6"
              />
            </svg>
            <div className="cancelled-note">{t('settings.workflow.anyStateCancel')}</div>
          </div>
          <div className="cancelled-state-wrapper">
            {renderStateNode(cancelledState, true)}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="diagram-legend">
        <div className="legend-item">
          <span className="legend-badge approvers-auto">0</span>
          <span className="legend-text">{t('settings.workflow.legend.auto')}</span>
        </div>
        <div className="legend-item">
          <span className="legend-badge approvers-one">1</span>
          <span className="legend-text">{t('settings.workflow.legend.oneApprover')}</span>
        </div>
        <div className="legend-item">
          <span className="legend-badge approvers-two">2</span>
          <span className="legend-text">{t('settings.workflow.legend.twoApprovers')}</span>
        </div>
      </div>
    </div>
  );
};

export default WorkflowDiagram;
